
import React, { useState, useRef, useEffect } from 'react'
import { getChatCompletion } from '../../helpers/openaiApiHandler'
import { redactPersonalInfo } from '../../helpers/contentSafety'
import { enqueueSnackbar } from 'notistack'
import './AskModal.css'

const AskModal = ({
  onClose,
  feedbackMessage,
  question,
  studentAnswer,
  onMessagesUpdate,
  quota = null,
  onQuotaExceeded = null
}) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: feedbackMessage,
      timestamp: new Date()
    }
  ])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (onMessagesUpdate && messages.length > 1) {
      onMessagesUpdate(messages)
    }
  }, [messages, onMessagesUpdate])

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return

    // Redact any personal information from the student's message
    const redactedContent = redactPersonalInfo(inputMessage)

    const userMessage = {
      role: 'user',
      content: redactedContent,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setIsLoading(true)

    try {
      const conversationHistory = [
        {
          role: "system",
          content: `You are a warm, encouraging math tutor who is also a life coach. The student is working on this problem:

Question: ${question}
Student's Answer: ${studentAnswer}

Your role is to guide the student toward the correct answer with empathy and understanding. Approach this as a growth opportunity. DO NOT give away the correct answer directly. Instead:

- Use encouraging language that acknowledges their effort ("I can see you're thinking about this carefully...")
- Address misconceptions gently without judgment ("Let's explore this approach together...")
- Ask thoughtful guiding questions that help them discover the answer
- Relate the problem to their understanding, not their shortcomings
- Provide hints that build confidence ("You're on the right track with... Let's think about...")
- Frame mistakes as learning moments ("This is a common place where students discover something new...")
- Celebrate their reasoning process, even when the answer isn't correct yet
- Be patient, supportive, and believe in their ability to understand

IMPORTANT - CONCEPTUAL HINTS ONLY:
- Keep all hints conceptual, NOT procedural
- NEVER give explicit step-by-step instructions like "divide both sides by 2" or "multiply by 3"
- Instead, guide students to think about underlying concepts: "What happens to an equation when we want to isolate a variable?" or "Think about what operation would help balance things out here"
- Ask questions that prompt understanding of WHY, not just HOW
- Help them recognize patterns and relationships rather than following recipes

STAYING ON TOPIC:
- If the student tries to change the subject or discuss unrelated topics, gently acknowledge their message and redirect them back to the math problem
- Say something like: "That's interesting! But let's get back to this problem - I want to make sure you feel confident about it. Now, thinking about what we were working on..."

PERSONAL INFORMATION SAFETY:
- If the student shares or attempts to share any personal information (name, age, location, school, contact info, etc.), do NOT acknowledge, repeat, or store that information
- Never repeat any personal information back to the student in any way. If they say that their name is "John", do not say "John" in your response
- Gently remind them: "I appreciate you sharing, but please don't share personal information with me. I'm here just to help you with math. Let's focus on this problem together."
- Then immediately redirect to the math problem

Remember: You're not just teaching math - you're building confidence and resilience. Keep responses concise but warm.`
        },
        ...messages.map(msg => ({
          role: msg.role,
          content: msg.content
        })),
        {
          role: 'user',
          content: redactedContent
        }
      ]

      const aiResponse = await getChatCompletion(conversationHistory, 'gpt-4', 0.1, quota)

      const assistantMessage = {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date()
      }

      setMessages(prev => [...prev, assistantMessage])
    } catch (error) {
      console.error('Error getting AI response:', error)
      if (error?.details?.reason === 'helper_quota_exceeded' && onQuotaExceeded) {
        onQuotaExceeded()
      }
      enqueueSnackbar({
        message: error?.details?.reason === 'helper_quota_exceeded'
          ? "You've used your 5 sessions for this week."
          : 'Error communicating with AI. Please try again.',
        variant: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className="ask-modal-overlay" onClick={onClose}>
      <div className="ask-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="ask-modal-header">
          <div className="ask-modal-header-left">
            <img src="/favicon.png" alt="Quillnote tutor" className="ask-modal-bird-avatar" />
            <div>
              <h2>Ask for Help</h2>
              <p className="ask-modal-header-subtitle">Your AI study buddy</p>
            </div>
          </div>
          <button className="ask-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="ask-modal-messages">
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`ask-message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
            >
              <div className="ask-message-row">
                {message.role === 'assistant' && (
                  <img src="/favicon.png" alt="" className="ask-message-avatar" />
                )}
                <div className="ask-message-content">
                  {message.content}
                </div>
              </div>
              <div className="ask-message-timestamp">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="ask-message assistant-message">
              <div className="ask-message-row">
                <img src="/favicon.png" alt="" className="ask-message-avatar" />
                <div className="ask-message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="ask-modal-input-container">
          <textarea
            className="ask-modal-input"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a question..."
            rows="3"
            disabled={isLoading}
          />
          <button 
            className="ask-modal-send-btn"
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
          >
            Send
          </button>
        </div>
        <div className="ask-modal-disclaimer">
          This is an AI chat helper. Remember that AI can make mistakes. Please double-check AI responses for accuracy.
        </div>
      </div>
    </div>
  )
}

export default AskModal
